// src/components/NumberField.jsx
//
// A number input with its own stepper. The native spinner is hidden: it is
// only drawn on hover in most browsers, differs between them, and ignores
// the page's type and colour entirely.
//
// Holding an arrow repeats, with a short delay before it starts and a faster
// interval after a second, so dragging a dimension from 3 to 12 does not mean
// twelve clicks. The repeat runs on a timer rather than an animation frame
// because it changes state, and state changes should not be tied to paint.

import React, { useCallback, useEffect, useRef } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const HOLD_DELAY = 380; // before repeating starts
const HOLD_RATE = 90; // between repeats
const HOLD_FAST_AFTER = 1200; // switch to the faster rate
const HOLD_FAST_RATE = 40;

export default function NumberField({
  label,
  suffix,
  value,
  onChange,
  min = 0,
  max = 9999,
  step = 0.1,
  placeholder,
  id,
}) {
  const timers = useRef({ start: null, tick: null, fast: null });

  const clear = useCallback(() => {
    const t = timers.current;
    clearTimeout(t.start);
    clearInterval(t.tick);
    clearTimeout(t.fast);
    t.start = t.tick = t.fast = null;
  }, []);

  useEffect(() => clear, [clear]);

  // Steps from the current value, or from the placeholder when the field is
  // empty, so the first press does something sensible rather than jumping to
  // the minimum.
  const stepBy = useCallback(
    (dir) => {
      const base =
        value === "" || value === null || value === undefined
          ? Number(placeholder) || 0
          : Number(value) || 0;
      const next = base + dir * step;
      const clamped = Math.min(max, Math.max(min, next));
      // Floating point: 2.9999999 is not a dimension anyone typed.
      const decimals = String(step).includes(".")
        ? String(step).split(".")[1].length
        : 0;
      onChange(Number(clamped.toFixed(decimals)));
    },
    [value, placeholder, step, min, max, onChange]
  );

  const startHold = (dir) => (e) => {
    // Left button or touch only, and do not let the press select the label.
    if (e.button != null && e.button !== 0) return;
    e.preventDefault();
    stepBy(dir);

    const t = timers.current;
    t.start = setTimeout(() => {
      t.tick = setInterval(() => stepBy(dir), HOLD_RATE);
      t.fast = setTimeout(() => {
        clearInterval(t.tick);
        t.tick = setInterval(() => stepBy(dir), HOLD_FAST_RATE);
      }, HOLD_FAST_AFTER);
    }, HOLD_DELAY);
  };

  const num = Number(value);
  const atMin = value !== "" && !Number.isNaN(num) && num <= min;
  const atMax = value !== "" && !Number.isNaN(num) && num >= max;

  return (
    <label className="block" htmlFor={id}>
      <span className="mb-1.5 block font-mono text-[9px] uppercase tracking-[0.14em] text-subtle">
        {label}
        {suffix && <span className="ml-1 opacity-70">{suffix}</span>}
      </span>

      <div className="group flex items-stretch overflow-hidden rounded-lg border border-line bg-surface transition focus-within:border-brooke-red focus-within:ring-2 focus-within:ring-brooke-red/25">
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          min={min}
          max={max}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-transparent px-3 py-3 font-mono text-[15px] tabular-nums text-ink outline-none placeholder:text-subtle [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        />

        <div className="flex w-8 shrink-0 flex-col border-l border-line">
          <Arrow
            dir="up"
            disabled={atMax}
            onPointerDown={startHold(1)}
            onPointerUp={clear}
            onPointerLeave={clear}
            onPointerCancel={clear}
          />
          <span className="h-px bg-line" />
          <Arrow
            dir="down"
            disabled={atMin}
            onPointerDown={startHold(-1)}
            onPointerUp={clear}
            onPointerLeave={clear}
            onPointerCancel={clear}
          />
        </div>
      </div>
    </label>
  );
}

// ---------------------------------------------------------------------------
function Arrow({ dir, disabled, ...handlers }) {
  const Icon = dir === "up" ? ChevronUp : ChevronDown;

  return (
    <button
      type="button"
      // Keeps the arrows out of the tab order: the input itself already takes
      // up and down keys, so tabbing through them would be noise.
      tabIndex={-1}
      aria-hidden="true"
      disabled={disabled}
      {...handlers}
      className="grid flex-1 place-items-center text-subtle transition-colors hover:bg-brooke-red/10 hover:text-ink active:bg-brooke-red/20 disabled:pointer-events-none disabled:opacity-25"
    >
      <Icon size={13} strokeWidth={2.2} />
    </button>
  );
}